import {useCallback, useMemo} from 'react'
import {useWatch} from 'react-hook-form'

import {useFieldConfigurationContext} from '../../configuration/useConfigurationContext'
import {useOrderProcessFormContext} from '../../form/useOrderProcessFormContext'
import {useMix} from '../mix/useMix'

import {PumpsConfiguration, UnloadingMethodsConfiguration} from './types'

/**
 * Special unloading method code ('O') - used when customer orders pump alongside concrete delivery
 */
export const OWN_CONTRACTED_PUMP = 'O'

/**
 * Hook for managing unloading method selection
 */
export const useUnloadingMethods = () => {
  const {control, setValue, trigger, formState: {defaultValues}} = useOrderProcessFormContext()
  const {selectedContract} = useMix()
  const unloadingMethodConfiguration = useFieldConfigurationContext('unloadingMethods')
  const pumpConfiguration = useFieldConfigurationContext('pumps')

  const availableUnloadingMethods = useMemo(
    () =>
      getAvailableUnloadingMethods(
        unloadingMethodConfiguration,
        pumpConfiguration,
        selectedContract?.salesOffice
      ),
    [unloadingMethodConfiguration, pumpConfiguration, selectedContract?.salesOffice]
  )

  const selectedUnloadingMethod = useWatch({
    name: 'unloadingMethod.unloadingMethodCode',
    control
  })

  const defaultUnloadingMethod = defaultValues?.unloadingMethod?.unloadingMethodCode

  const setUnloadingMethodCode = useCallback(
    (code?: string) => {
      if (!unloadingMethodConfiguration.isEnabled) {
        console.warn(
          '[useUnloadingMethods] setUnloadingMethodCode: Unloading method configuration is disabled.',
          {
            attemptedCode: code
          }
        )
        return
      }

      if (code && !availableUnloadingMethods.includes(code)) {
        console.warn(
          '[useUnloadingMethods] setUnloadingMethodCode: Unloading method is not available',
          {
            attemptedCode: code,
            availableMethods: availableUnloadingMethods
          }
        )
        return
      }

      setValue('unloadingMethod.unloadingMethodCode', code)
      void trigger()
    },
    [setValue, trigger, unloadingMethodConfiguration.isEnabled, availableUnloadingMethods]
  )

  return {
    selectedUnloadingMethod,
    defaultUnloadingMethod,
    availableUnloadingMethods,
    setUnloadingMethodCode,
    configuration: unloadingMethodConfiguration
  }
}

export const getAvailableUnloadingMethods = (
  unloadingMethodsConfiguration: UnloadingMethodsConfiguration['unloadingMethods'],
  pumpsConfiguration: PumpsConfiguration['pumps'],
  salesOffice?: string
): string[] => {
  if (!unloadingMethodsConfiguration.isEnabled) {
    return []
  }

  let methods = unloadingMethodsConfiguration.default || []

  // If we have sales office specific methods, use those instead
  if (salesOffice && unloadingMethodsConfiguration.salesOfficeUnloadingMethods?.[salesOffice]) {
    methods = unloadingMethodsConfiguration.salesOfficeUnloadingMethods[salesOffice]
  }

  // Filter out pump option ('O') when pump is enabled
  return methods.filter((method) => !pumpsConfiguration.isEnabled || method !== OWN_CONTRACTED_PUMP)
}
